import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  CallOutlined as CallIcon,
  InfoOutlined as InfoIcon,
  MailOutline as MailIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { userManagementAPI } from '../../services/api';
import './BrokerManagement.scss';

const getUsersFromResponse = (response) => {
  const payload = response?.data?.data ?? response?.data;

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.users)) {
    return payload.users;
  }

  if (Array.isArray(payload?.results)) {
    return payload.results;
  }

  return [];
};

const mapStatus = (user) => {
  if (user?.is_deleted === true || user?.isDeleted === true || user?.deletedAt || user?.deleted_at) {
    return 'Deleted';
  }

  if (typeof user?.is_deleted === 'number' && user.is_deleted === 1) {
    return 'Deleted';
  }

  if (typeof user?.is_active === 'number') {
    return user.is_active === 1 ? 'Active' : 'Inactive';
  }

  if (typeof user?.is_active === 'boolean') {
    return user.is_active ? 'Active' : 'Inactive';
  }

  if (typeof user?.status === 'string') {
    return user.status;
  }

  if (typeof user?.isBlocked === 'boolean') {
    return user.isBlocked ? 'Inactive' : 'Active';
  }

  if (typeof user?.isActive === 'boolean') {
    return user.isActive ? 'Active' : 'Inactive';
  }

  return 'Active';
};

const normalizeStatusLabel = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (normalized === 'active') return 'Active';
  if (normalized === 'inactive' || normalized === 'blocked' || normalized === 'in-active') return 'Inactive';
  if (normalized === 'deleted' || normalized === 'deactivated' || normalized === 'disabled') return 'Deleted';

  return value || 'Active';
};

const mapName = (user) => {
  if (user?.name) {
    return user.name;
  }

  const fullName = [
    user?.firstName || user?.first_name,
    user?.lastName || user?.last_name,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (fullName) {
    return fullName;
  }

  return user?.user_name || '--';
};

const getInitials = (value) => {
  const safeName = String(value || '').trim();
  if (!safeName) {
    return 'B';
  }

  return safeName
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

const isBroker = (user) => {
  const candidates = [
    user?.user_type,
    user?.userType,
    user?.type,
    user?.role,
    user?.role_name,
    user?.roleName,
  ];

  return candidates.some((value) => {
    let normalizedValue = String(value || '');
    normalizedValue = normalizedValue.trim();
    normalizedValue = normalizedValue.toLowerCase();
    return normalizedValue === 'broker';
  });
};

const hasBrokerProfile = (user) => {
  const profileReference =
    user?.brokerProfileId
    || user?.broker_profile_id
    || user?.profileId
    || user?.brokerProfile?.id;

  return Boolean(profileReference);
};

const BrokerManagement = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [brokers, setBrokers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchBrokers = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await userManagementAPI.getUsers(false);
        const users = getUsersFromResponse(response);
        setBrokers(users.filter(isBroker));
      } catch (fetchError) {
        const apiMessage = fetchError?.response?.data?.message;
        setError(apiMessage || 'Unable to load brokers. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchBrokers();
  }, []);

  const filteredBrokers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return brokers;
    }

    return brokers.filter((broker) => {
      const fullName = mapName(broker).toLowerCase();
      const email = String(broker?.email || broker?.email_id || '').toLowerCase();
      const phone = String(broker?.phone || broker?.mobile || broker?.phoneNumber || '').toLowerCase();
      return fullName.includes(query) || email.includes(query) || phone.includes(query);
    });
  }, [brokers, searchTerm]);

  return (
    <Box className="broker-management-page">
      <Box className="broker-management-content">
        <header className="broker-page-header">
          <div className="broker-header-content">
            <h1 className="broker-page-title">Broker Management</h1>
            <p className="broker-page-subtitle">{filteredBrokers.length} registered brokers</p>
          </div>
        </header>

        <div className="broker-search-wrap">
          <SearchIcon className="broker-search-icon" fontSize="small" />
          <input
            className="broker-search-input"
            placeholder="Search brokers..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>

        {loading ? (
          <Box className="broker-management-state">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : filteredBrokers.length === 0 ? (
          <Box className="broker-empty-state">
            <Typography>No brokers found</Typography>
          </Box>
        ) : (
          <div className="broker-cards-grid">
            {filteredBrokers.map((broker, index) => {
              const fullName = mapName(broker);
              const status = normalizeStatusLabel(mapStatus(broker));
              const key = String(broker?.id || broker?.userId || broker?.user_id || broker?.email || `${fullName}-${index}`);

              return (
                <article className="broker-card" key={key}>
                  <div className="broker-card-top">
                    <div className="broker-user-cell">
                      <span className="broker-user-avatar">{getInitials(fullName)}</span>
                      <div>
                        <h3 className="broker-user-name">{fullName}</h3>
                        <div className="broker-status-row">
                          <span className={`broker-status-dot ${status.toLowerCase()}`} />
                          <span className="broker-status-text">{status}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="broker-contact-cell">
                    <p>
                      <MailIcon fontSize="inherit" />
                      {broker?.email || broker?.email_id || '--'}
                    </p>
                    <p>
                      <CallIcon fontSize="inherit" />
                      {broker?.phone || broker?.mobile || broker?.phoneNumber || '--'}
                    </p>
                  </div>

                  {!hasBrokerProfile(broker) && (
                    <div className="broker-profile-alert">
                      <span>
                        <InfoIcon fontSize="inherit" />
                        No broker profile created yet
                      </span>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </Box>
    </Box>
  );
};

export default BrokerManagement;
